import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { copiesFor, printerFor, qzActive, qzConnect, qzPrint, repeat } from "@/lib/qz";
import { orderSlip, saleReceipt, comandaBill, deliveryOrder, partialReceipt, reprintHead } from "@/lib/receipts";
import { bgTimer, setPrintHandler, syncPrintPush } from "@/lib/printPush";
import { PrintNotifyPrompt } from "@/components/PrintNotifyPrompt";

export const CENTRAL_KEY = "pdv_print_central";
export const isCentral = () => localStorage.getItem(CENTRAL_KEY) !== "0";
export const setCentral = (on) => { localStorage.setItem(CENTRAL_KEY, on ? "1" : "0"); window.dispatchEvent(new Event("pdv-central")); };

const NO_QZ = "QZ Tray não conectado neste computador";

function routesFor(job, t) {
  const p = job.payload;
  const store = t.nome_fantasia;
  if (job.kind === "order") {
    const o = p.order;
    const pre = p.reprint ? "2a VIA " : "";
    return [...(o.items.length ? [["comanda", (pr) => orderSlip(o, `${pre}COMANDA`, o.items, store, pr)]] : []),
      ...(o.sectors || []).map((s) => [`sector_${s.id}`, (pr) => orderSlip(o, `${pre}${s.sector.toUpperCase()}`, s.items, store, pr)])];
  }
  const head = (pr) => (p.reprint ? reprintHead(pr) : []);
  if (job.kind === "bill") return [["comanda", (pr) => [...head(pr), ...comandaBill(p.comanda, t, pr)]]];
  if (job.kind === "sale") return [["pdv", (pr) => [...head(pr), ...saleReceipt(p.sale, t, pr)]]];
  if (job.kind === "partial") return [["pdv", (pr) => [...head(pr), ...partialReceipt(p.partial, t, pr)]]];
  return [["delivery", (pr) => deliveryOrder(p.order, store, pr, !!p.reprint)]];
}

async function runJob(job, t) {
  for (const [route, build] of routesFor(job, t)) {
    const copies = copiesFor(t.settings, route);
    if (!copies) continue;
    const pr = printerFor(t.settings, route) || printerFor(t.settings, "pdv");
    if (!pr) throw new Error("Impressão direta desligada ou sem impressora configurada neste computador");
    await qzPrint(pr.name, repeat(build(pr), copies));
  }
}

async function jobStatus(id) {
  const { data } = await api.get("/print-jobs");
  const job = data.jobs.find((x) => x.id === id);
  return { job, printed: !!job && ["done", "printing"].includes(job.status), error: job?.error || "" };
}

function makeRunner(session, on) {
  const device = `Computador de ${session.user.name}`;
  let running = null;
  let lastTry = 0;
  const tick = async (force) => {
    if (!session.tenant.settings?.printing?.enabled) return "impressão direta desligada em Configurações → Impressão";
    if (!qzActive()) {
      if (!force && Date.now() - lastTry < 30000) return NO_QZ;
      lastTry = Date.now();
      if (!(await qzConnect())) return NO_QZ;
    }
    let jobs;
    try { ({ data: jobs } = await api.post("/print-jobs/claim", { device })); } catch { return "sem conexão com o servidor"; }
    for (const job of jobs) {
      try {
        await runJob(job, session.tenant);
        await api.post(`/print-jobs/${job.id}/result`, { ok: true });
      } catch (e) {
        await api.post(`/print-jobs/${job.id}/result`, { ok: false, error: String(e?.message || e).slice(0, 280) }).catch(() => {});
      }
    }
    return "";
  };
  const run = async (force, idle) => {
    if (idle && running) return "";
    while (running) await running;
    running = tick(force).finally(() => { running = null; });
    return running;
  };
  const printNow = async (id) => {
    const { job } = await jobStatus(id).catch(() => ({}));
    if (!job) { toast.error("Não encontrei essa impressão na fila"); return { alive: true, printed: false }; }
    const name = job.title || "Impressão";
    if (job.status === "done") { toast.info(`${name} já foi impresso`); return { alive: true, printed: true }; }
    if (["failed", "expired"].includes(job.status)) await api.post(`/print-jobs/${id}/retry`).catch(() => {});
    const why = await run(true);
    const r = await jobStatus(id).catch(() => ({ printed: false }));
    if (r.printed) toast.success(`${name} impresso`);
    else toast.error(`${name} não saiu: ${why || r.error || "verifique o QZ Tray e a impressora"}`);
    return { alive: true, printed: r.printed };
  };
  const answer = async (msg) => {
    if (msg.test) return { alive: true, printed: false };
    if (msg.force) return printNow(msg.job_id);
    if (!on) return { alive: true, printed: false, error: "impressão do app desligada neste computador" };
    const why = await run(true);
    const r = await jobStatus(msg.job_id).catch(() => ({ printed: false }));
    return { alive: true, printed: r.printed, error: r.printed ? "" : why || r.error };
  };
  return { run, printNow, answer };
}

export function CentralPrinter({ session }) {
  const [on, setOn] = useState(isCentral);
  const [params, setParams] = useSearchParams();
  const printId = params.get("print");
  const runner = useRef(null);
  const handled = useRef("");
  useEffect(() => {
    const sync = () => setOn(isCentral());
    window.addEventListener("pdv-central", sync);
    return () => window.removeEventListener("pdv-central", sync);
  }, []);
  useEffect(() => { syncPrintPush(on).catch(() => {}); }, [on, session.tenant.id]);
  useEffect(() => {
    const r = makeRunner(session, on);
    runner.current = r;
    setPrintHandler(r.answer);
    let stop = null;
    if (on) { r.run(false); stop = bgTimer(() => r.run(false, true), 4000); }
    return () => { stop?.(); setPrintHandler(null); };
  }, [on, session]);
  useEffect(() => {
    if (!printId || handled.current === printId) return;
    handled.current = printId;
    runner.current?.printNow(printId);
    setParams((p) => { p.delete("print"); return p; }, { replace: true });
  }, [printId, setParams]);
  return <PrintNotifyPrompt central={on} />;
}
